<template>
    <div :class="['credit-card-required-container', { 'admin-view': hasPermission }]">
        <div class="warning-card">
            <div class="card-body">
                <div class="card-info">
                    <div class="alert-icon">
                        <svg
                            viewBox="0 0 24 24"
                            width="32"
                            height="32"
                            stroke="#e74c3c"
                            stroke-width="2"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path
                                d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                            ></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                    </div>
                    <div class="card-message">
                        <h3>{{ $t("message.creditCardRequired") }}</h3>
                        <p v-if="hasPermission">
                            {{ $t("message.creditCardRequiredDescription") }}
                        </p>
                        <p v-else>
                            {{ $t("message.creditCardRequiredDescription2") }}
                        </p>
                    </div>
                </div>

                <div v-if="hasPermission" class="card-actions">
                    <SupsisButton primary @click="openPaymentModal">{{ $t("message.addCreditCard") }}</SupsisButton>
                    <SupsisButton ghost @click="openManageModal">{{ $t("message.subscriptionManagement") }}</SupsisButton>
                </div>

                <div v-if="!hasPermission" class="no-permission-info">
                    <p v-html="$t('message.noPermissionDescription')"></p>
                    <div class="support-action">
                        <SupsisButton primary @click="connectSupport">{{ $t("message.liveSupport") }}</SupsisButton>
                    </div>
                </div>
            </div>
        </div>

        <div class="features-container">
            <div class="feature-box">
                <div class="feature-icon">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#22272d"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                    </svg>
                </div>
                <h4>{{ $t("message.annualDiscount") }}</h4>
                <p>{{ $t("message.annualDiscountDescription") }}</p>
            </div>

            <div class="feature-box">
                <div class="feature-icon">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#22272d"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                    </svg>
                </div>
                <h4>{{ $t("message.payOnlyForUsedFeatures") }}</h4>
                <p>{{ $t("message.payOnlyForUsedFeaturesDescription") }}</p>
            </div>

            <div class="feature-box">
                <div class="feature-icon">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#22272d"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <polyline points="18 15 12 9 6 15"></polyline>
                    </svg>
                </div>
                <h4>{{ $t("message.cancelAnytime") }}</h4>
                <p>{{ $t("message.cancelAnytimeDescription") }}</p>
            </div>
        </div>

        <Modal2
            v-if="isModalVisible"
            @cancel="closeModal"
            :showFooter="false"
            :showTitle="false"
            className="payment-modal"
        >
            <PaymentSteps ref="paymentSteps" @cancel="closeModal" @submit="savePaymentInfo" />
        </Modal2>
        <!-- Abonelik Yönetimi Modal -->
        <SubscriptionManagementModal
            :visible="isManageModalVisible"
            @cancel="isManageModalVisible = false"
            @unsubscribe="handleUnsubscribe"
        />
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import { publishMessage, SystemMessageTypes } from "../../utils/systemPubSub";
import SupsisButton from "../../views/Pages/Settings/components/SupsisButton.vue";
import Modal2 from "../../components/common/Modal2.vue";
import PaymentSteps from "./forms/PaymentSteps.vue";
import SubscriptionManagementModal from "./SubscriptionManagementModal.vue";

export default {
    name: "CreditCardRequiredWarning",
    components: {
        SupsisButton,
        Modal2,
        PaymentSteps,
        SubscriptionManagementModal,
    },
    computed: {
        ...mapGetters({
            checkPermission: "auth/checkPermission",
            paidSubscriptions: "site/paidSubscriptions",
        }),
        ...mapState({
            isAdmin: (state) => state.auth.adminUser != null,
            isRoot: (state) => state.auth.user.role.isRoot,
            user: (state) => state.auth.user,
            settings: (state) => state.setting.settings,
            site: (state) => state.site.site,
            channels: (state) => state.channel.channels,
            users: (state) => state.user.users.data,
        }),
        hasPermission() {
            return this.isAdmin || this.isRoot || this.checkPermission("billing.read");
        },
    },
    data() {
        return {
            isModalVisible: false,
            isManageModalVisible: false,
        };
    },
    methods: {
        ...mapActions({
            unsubscribePlan: "site/unsubscribePlan",
            updateSettings: "setting/updateSettings",
            deleteChannel: "channel/deleteChannel",
            updateUser: "user/updateUser",
        }),
        connectSupport() {
            publishMessage(SystemMessageTypes.CONNECT_SUPPORT_TEAM, {
                initialMessage: "Kredi kartı ekleme konusunda yardıma ihtiyacım var",
                largeMode: true,
            });
        },
        openPaymentModal() {
            this.isModalVisible = true;
        },
        closeModal() {
            this.isModalVisible = false;
        },
        savePaymentInfo(paymentData) {
            if (paymentData.step === "billing-address") {
                const billingPayload = {
                    ...paymentData,
                    billingType: paymentData.isCompany ? "company" : "individual",
                };

                this.$store
                    .dispatch("site/updateBilling", billingPayload)
                    .then(() => {
                        this.$notification.success({
                            message: this.$t("billing.success"),
                            description: this.$t("message.billingInfoSavedSuccessfully"),
                            duration: 4.5,
                        });
                        this.$refs.paymentSteps.nextStep();
                    })
                    .catch((error) => {
                        this.$notification.error({
                            message: "Hata",
                            description: this.$t("message.billingInfoSaveError"),
                            duration: 4.5,
                        });
                        console.error("Fatura bilgisi kaydetme hatası:", error);
                    });
            } else if (paymentData.step === "credit-card") {
                const cardPayload = {
                    alias: paymentData.cvc,
                    holderName: paymentData.holderName,
                    cardNumber: paymentData.cardNumber,
                    expireMonth: paymentData.expireMonth,
                    expireYear: paymentData.expireYear,
                };

                this.$store
                    .dispatch("site/updateCardInfo", cardPayload)
                    .then((result) => {
                        if (result.status === "error") {
                            if (this.$refs.paymentSteps && typeof this.$refs.paymentSteps.showError === "function") {
                                this.$refs.paymentSteps.showError(
                                    result.message || this.$t("message.cardInfoUpdateError")
                                );
                            }
                        } else {
                            this.$notification.success({
                                message: this.$t("billing.success"),
                                description: this.$t("message.paymentInfoSavedSuccessfully"),
                                duration: 4.5,
                            });
                            this.closeModal();
                        }
                    })
                    .catch((error) => {
                        if (this.$refs.paymentSteps && typeof this.$refs.paymentSteps.showError === "function") {
                            this.$refs.paymentSteps.showError(this.$t("message.cardInfoUpdateError"));
                        }
                        console.error("Kart bilgisi güncelleme hatası:", error);
                    });
            }
        },
        openManageModal() {
            this.isManageModalVisible = true;
        },
        handleUnsubscribe(payload) {
            this.unsubscribePlan({
                planId: payload.planId,
                immediateCancel: true,
            });
        },
    },
};
</script>

<style scoped>
.credit-card-required-container {
    width: 100%;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    height: 100%;
}

.warning-card {
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    width: 100%;
    max-width: 900px;
    margin-bottom: 40px;
}

.card-body {
    padding: 24px;
}

.card-info {
    display: flex;
    align-items: flex-start;
    margin-bottom: 16px;
}

.alert-icon {
    margin-right: 16px;
    flex-shrink: 0;
}

.card-message h3 {
    margin: 0 0 8px 0;
    font-size: 18px;
    color: #333;
    font-weight: 600;
}

.card-message p {
    margin: 0;
    font-size: 15px;
    line-height: 1.5;
    color: #555;
}

.card-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-start;
    margin-left: 48px;
}

.no-permission-info {
    margin-left: 48px;
    border-top: 1px solid #eee;
    padding-top: 16px;
    margin-top: 16px;
}

.no-permission-info p {
    margin: 0 0 16px 0;
    font-size: 14px;
    color: #555;
}

.support-action {
    display: flex;
    justify-content: flex-start;
}

.features-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
    width: 100%;
    max-width: 900px;
}

.feature-box {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 20px;
    width: 100%;
    max-width: 280px;
    text-align: center;
    transition: transform 0.2s;
}

.feature-box:hover {
    transform: translateY(-5px);
}

.feature-icon {
    margin-bottom: 15px;
}

h4 {
    margin: 0 0 10px 0;
    font-size: 16px;
    color: #22272d;
    font-weight: 600;
}

.feature-box p {
    font-size: 14px;
    color: #666;
    margin: 0;
}

strong {
    color: #e74c3c;
    font-weight: 600;
}

.payment-modal {
    .ant-modal {
        width: 80% !important;
        top: 30px !important;
    }
}
</style>
